import React, { useEffect, useMemo, useState } from 'react';
import { Loader2, Palmtree, Users, CalendarClock, ShieldAlert } from 'lucide-react';
import { atlasApi, AtlasUser, FeriasProgramada } from '../shared/atlasApi';

interface Props {
  user: AtlasUser;
  podeEditar: boolean;
  onAbrirFicha: (funccodi: number) => void;
}

function formatarData(s?: string | null): string {
  if (!s) return '—';
  try { return new Date(s).toLocaleDateString('pt-BR'); } catch { return s; }
}

const StatCard: React.FC<{ icon: React.ReactNode; titulo: string; valor: React.ReactNode; cor: string }> = ({ icon, titulo, valor, cor }) => (
  <div className="bg-white rounded-2xl border border-gray-200 p-4 flex items-center gap-3">
    <div className={`w-11 h-11 rounded-xl flex items-center justify-center text-white ${cor}`}>{icon}</div>
    <div>
      <div className="text-[11px] text-gray-400 uppercase tracking-wider font-semibold">{titulo}</div>
      <div className="text-2xl font-bold text-gray-900">{valor}</div>
    </div>
  </div>
);

const FeriasPage: React.FC<Props> = ({ user, podeEditar, onAbrirFicha }) => {
  const [programadas, setProgramadas] = useState<FeriasProgramada[]>([]);
  const [totalAtivos, setTotalAtivos] = useState<number | null>(null);
  const [departamento, setDepartamento] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState<string | null>(null);

  useEffect(() => {
    if (!podeEditar) return;
    let cancel = false;
    setLoading(true);
    setErro(null);
    Promise.all([
      atlasApi.listarFeriasProgramadas(user, 200),
      atlasApi.listarColaboradores(user, { status: 'ativo', per_page: 1 }),
    ])
      .then(([prog, ativos]) => {
        if (cancel) return;
        setProgramadas(prog.itens || []);
        setTotalAtivos(ativos.total ?? null);
      })
      .catch(e => { if (!cancel) setErro(e.message || 'Erro ao carregar'); })
      .finally(() => { if (!cancel) setLoading(false); });
    return () => { cancel = true; };
  }, [user, podeEditar]);

  const departamentos = useMemo(() => {
    const map = new Map<string, string>();
    programadas.forEach(p => {
      if (p.centro_custo_codigo != null) map.set(String(p.centro_custo_codigo), p.centro_custo_nome || String(p.centro_custo_codigo));
    });
    return Array.from(map.entries()).map(([codigo, nome]) => ({ codigo, nome }));
  }, [programadas]);

  const programadasFiltradas = useMemo(() => {
    if (!departamento) return programadas;
    return programadas.filter(p => String(p.centro_custo_codigo) === departamento);
  }, [programadas, departamento]);

  const proximas30 = useMemo(() => {
    const limite = Date.now() + 30 * 86400000;
    return programadasFiltradas.filter(p => {
      if (!p.inicio_programado) return false;
      const t = new Date(p.inicio_programado).getTime();
      return t >= Date.now() && t <= limite;
    }).length;
  }, [programadasFiltradas]);

  if (!podeEditar) {
    return (
      <div className="p-6">
        <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6 max-w-2xl flex items-start gap-3">
          <ShieldAlert className="w-6 h-6 text-amber-600 flex-shrink-0" />
          <div>
            <h3 className="font-bold text-amber-800">Acesso restrito</h3>
            <p className="text-sm text-amber-700 mt-1">Esta tela é exclusiva do RH. Suas próprias férias estão na sua ficha.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-4 max-w-5xl">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl bg-emerald-50 text-emerald-600 flex items-center justify-center">
          <Palmtree className="w-5 h-5" />
        </div>
        <div className="flex-1">
          <h2 className="text-lg font-bold text-gray-900">Férias</h2>
          <p className="text-sm text-gray-500">Programação e acompanhamento do time.</p>
        </div>
        <select
          value={departamento}
          onChange={e => setDepartamento(e.target.value)}
          className="px-3 py-2 rounded-lg border border-gray-200 text-sm text-gray-700 bg-white focus:outline-none focus:ring-2 focus:ring-purple-200"
        >
          <option value="">Todos os departamentos</option>
          {departamentos.map(d => <option key={d.codigo} value={d.codigo}>{d.nome}</option>)}
        </select>
      </div>

      {erro && <div className="bg-rose-50 border border-rose-200 rounded-lg px-3 py-2 text-sm text-rose-700">{erro}</div>}

      {loading ? (
        <div className="p-12 text-center text-gray-400"><Loader2 className="w-6 h-6 animate-spin mx-auto mb-2" /> Carregando...</div>
      ) : (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <StatCard icon={<Users className="w-5 h-5" />} titulo="Colaboradores ativos" valor={totalAtivos ?? '—'} cor="bg-purple-500" />
            <StatCard icon={<CalendarClock className="w-5 h-5" />} titulo="Férias programadas" valor={programadasFiltradas.length} cor="bg-emerald-500" />
            <StatCard icon={<CalendarClock className="w-5 h-5" />} titulo="Nos próximos 30 dias" valor={proximas30} cor="bg-amber-500" />
          </div>

          <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
            <div className="px-4 py-3 border-b border-gray-100">
              <h3 className="font-bold text-gray-900 text-sm">Férias programadas próximas</h3>
            </div>
            {programadasFiltradas.length === 0 ? (
              <div className="p-10 text-center text-gray-400 text-sm">Nenhuma férias programada encontrada.</div>
            ) : (
              <table className="w-full text-sm">
                <thead className="bg-gray-50 text-[11px] text-gray-500 uppercase tracking-wider">
                  <tr>
                    <th className="px-4 py-2 text-left">Colaborador</th>
                    <th className="px-4 py-2 text-left">Cargo / Departamento</th>
                    <th className="px-4 py-2 text-left">Início programado</th>
                    <th className="px-4 py-2 text-right">Dias</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {programadasFiltradas.map((p, i) => (
                    <tr
                      key={`${p.funccodi}-${i}`}
                      onClick={() => onAbrirFicha(p.funccodi)}
                      className="hover:bg-purple-50/40 cursor-pointer transition"
                    >
                      <td className="px-4 py-3 font-medium text-gray-900">{p.nome || `#${p.funccodi}`}</td>
                      <td className="px-4 py-3 text-gray-600">
                        <div>{p.cargo_nome || '—'}</div>
                        <div className="text-xs text-gray-400">{p.centro_custo_nome || '—'}</div>
                      </td>
                      <td className="px-4 py-3 text-gray-700">{formatarData(p.inicio_programado)}</td>
                      <td className="px-4 py-3 text-right text-gray-700">{p.dias ?? '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          <p className="text-[11px] text-gray-400">
            O saldo e o vencimento individuais de cada colaborador estão na aba "Férias" da ficha.
          </p>
        </>
      )}
    </div>
  );
};

export default FeriasPage;
